import React, { useState, useEffect, useCallback } from 'react';
import api from '../../../config/axiosConfig';
import { API_URLS } from '../../../config/Constants';
import { sessionManager } from '../../../config/sessionManager';
import { showErrorMsg } from '../../../config/common';
import QuarterSelector from '../QuarterSelector';
import WomensPlayerTables from '../../../components/shared/WomensPlayerTables';
import PlayerNameButton from '../../../components/playerSheet/PlayerNameButton';

// Player types for each section
interface PlayerBase {
  playerNumber: string;
  playerName: string;
  playerId: string;
}

interface PossessionPlayer extends PlayerBase {
  groundBalls: number;
  turnovers: number;
  causedTurnovers: number;
  shotsSaved: number;
  npc: number;
}

interface OffensePlayer extends PlayerBase {
  shots: number;
  saved: number;
  goals: number;
  assists: number;
  points: number;
  shotPercentage: number;
  sogPercentage: number;
}

interface GoaliePlayer extends PlayerBase {
  shotsOnGoal: number;
  saved: number;
  goalsAllowed: number;
  savePercentage: number;
}

interface BlockedShotsPlayer extends PlayerBase {
  blocks: number;
}

// Table component
function StatsTable<T extends PlayerBase>({
  title,
  columns,
  players,
  isLoading,
  sortConfig,
  onSortChange,
  cellPaddingY = 'py-0.5',
  cellPaddingX = 'px-0.5',
  cellFontSize = 'text-lg',
}: {
  title: string;
  columns: {
    header: string;
    key: keyof T | 'special';
    render?: (player: T) => React.ReactNode;
    sortable?: boolean;
  }[];
  players: T[];
  isLoading?: boolean;
  sortConfig?: {
    key: string;
    direction: 'asc' | 'desc';
  } | null;
  onSortChange?: (sortConfig: { key: string; direction: 'asc' | 'desc' } | null) => void;
  cellPaddingY?: string;
  cellPaddingX?: string;
  cellFontSize?: string;
}) {
  const toSortableNumber = (value: unknown): number | null => {
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : null;
    }

    if (typeof value !== 'string') return null;

    const cleaned = value.trim().replace(/,/g, '').replace(/%$/, '');
    if (cleaned === '') return null;

    const parsed = Number(cleaned);
    return Number.isFinite(parsed) ? parsed : null;
  };

  // Sort players based on current sort configuration
  const sortedPlayers = React.useMemo(() => {
    if (!sortConfig) return players;

    return [...players].sort((a, b) => {
      const { key, direction } = sortConfig;

      // Handle special player column sorting
      if (key === 'special') {
        const aValue = `${a.playerNumber} ${a.playerName}`;
        const bValue = `${b.playerNumber} ${b.playerName}`;
        const aStr = aValue.toLowerCase();
        const bStr = bValue.toLowerCase();
        if (aStr < bStr) return direction === 'asc' ? -1 : 1;
        if (aStr > bStr) return direction === 'asc' ? 1 : -1;
        return 0;
      }

      let aValue: any = a[key as keyof T];
      let bValue: any = b[key as keyof T];

      // Compare numeric values (supports numbers and numeric-like strings, including percents)
      const aNumber = toSortableNumber(aValue);
      const bNumber = toSortableNumber(bValue);
      if (aNumber !== null && bNumber !== null) {
        return direction === 'asc' ? aNumber - bNumber : bNumber - aNumber;
      }

      // String comparison
      const aStr = String(aValue || '').toLowerCase();
      const bStr = String(bValue || '').toLowerCase();
      if (aStr < bStr) return direction === 'asc' ? -1 : 1;
      if (aStr > bStr) return direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [players, sortConfig]);

  const handleSort = (key: keyof T | 'special', idx: number) => {
    const column = columns[idx];
    if (!column || column.sortable === false) return;

    if (onSortChange) {
      let direction: 'asc' | 'desc' = 'desc'; // Default to desc for stats
      const sortKey = key === 'special' ? 'special' : String(key);
      if (sortConfig?.key === sortKey && sortConfig.direction === 'desc') {
        direction = 'asc';
      }
      onSortChange({ key: sortKey, direction });
    }
  };

  const getSortIcon = (key: keyof T | 'special', idx: number) => {
    const column = columns[idx];
    if (!column || column.sortable === false) return null;

    const sortKey = key === 'special' ? 'special' : String(key);
    if (sortConfig?.key === sortKey) {
      return sortConfig.direction === 'asc' ? <span className="ml-1">↑</span> : <span className="ml-1">↓</span>;
    }
    return <span className="ml-1 opacity-50">↕</span>;
  };
  return (
    <div className="mb-4">
      <div className="bg-sd-navy text-white text-lg font-bold px-4 py-2">{title}</div>
      <div className="overflow-x-auto relative shadow-xl">
        {/* Loading Spinner */}
        {isLoading && (
          <div role="status" className="absolute -translate-x-1/2 -translate-y-1/2 top-2/4 left-1/2 z-50">
            <svg
              aria-hidden="true"
              className="w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600"
              viewBox="0 0 100 101"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                fill="currentColor"
              />
              <path
                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                fill="currentFill"
              />
            </svg>
            <span className="sr-only">Loading...</span>
          </div>
        )}
        <div className={`${isLoading ? 'opacity-20 pointer-events-none' : ''}`}>
          <table className="text-center border border-[#22c55e] w-full">
            <thead>
              <tr className="bg-sd-navy text-white">
                {columns.map((col, idx) => {
                  return (
                    <th
                      key={idx}
                      className={`py-0.5 px-0.5 border border-[#22c55e] text-xs font-semibold ${
                        col.sortable !== false ? 'cursor-pointer hover:bg-blue-700 transition-colors' : ''
                      } ${idx === 0 ? 'text-left text-nowrap' : 'text-center'}`}
                      onClick={() => (col.sortable !== false ? handleSort(col.key, idx) : undefined)}
                    >
                      <div className="flex items-center justify-center">
                        {col.header}
                        {col.sortable !== false && getSortIcon(col.key, idx)}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            {players.length === 0 && (
              <tbody>
                <tr>
                  <td colSpan={columns.length} className="py-4 text-sm text-gray-500 text-center italic">
                    No players found for this section.
                  </td>
                </tr>
              </tbody>
            )}
            <tbody className="text-xs bg-[#f6fff0]">
              {sortedPlayers.map((player, idx) => (
                <tr key={player.playerId} className={idx % 2 === 1 ? 'bg-[#e6fce0]' : 'bg-white'}>
                  {columns.map((col, colIdx) => {
                    return (
                      <td
                        key={colIdx}
                        className={`${cellPaddingY} ${cellPaddingX} ${cellFontSize} border border-gray-400 ${
                          colIdx === 0 ? 'text-left text-sm max-w-[60px] whitespace-pre-wrap' : 'text-center'
                        }`}
                      >
                        {col.render ? col.render(player) : String(player[col.key as keyof T])}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// Main Component
const PlayersPageMobile: React.FC = () => {
  const clientId = sessionManager.getClientId();
  const seasonId = sessionManager.getSeasonId();
  const gameId = sessionManager.getGameId();

  const [periods, setPeriods] = useState<string[]>([]);
  const [activeQuarter, setActiveQuarter] = useState('Totals');
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Section data
  const [possessionPlayers, setPossessionPlayers] = useState<PossessionPlayer[]>([]);
  const [offensePlayers, setOffensePlayers] = useState<OffensePlayer[]>([]);
  const [goaliePlayers, setGoaliePlayers] = useState<GoaliePlayer[]>([]);
  const [rawPlayers, setRawPlayers] = useState<any[]>([]);
  const [blockedShotsPlayers, setBlockedShotsPlayers] = useState<BlockedShotsPlayer[]>([]);

  // Sort configurations for each table
  const [possessionSortConfig, setPossessionSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  } | null>({ key: 'playerNumber', direction: 'asc' });
  const [offenseSortConfig, setOffenseSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  } | null>({ key: 'playerNumber', direction: 'asc' });
  const [goalieSortConfig, setGoalieSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  } | null>({ key: 'playerNumber', direction: 'asc' });
  const [blockedShotsSortConfig, setBlockedShotsSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  } | null>({ key: 'playerNumber', direction: 'asc' });

  // Common player column definition
  const createPlayerColumn = <T extends PlayerBase>() => ({
    header: 'Players',
    key: 'special' as keyof T,
    render: (player: T) => (
      <PlayerNameButton playerSeasonId={player.playerId} seasonId={seasonId}>
        <span>
          <b>{player.playerNumber}</b> {player.playerName}
        </span>
      </PlayerNameButton>
    ),
  });

  // Column definitions
  const possessionColumns = [
    { ...createPlayerColumn<PossessionPlayer>(), sortable: true },
    {
      header: 'GB',
      key: 'groundBalls' as keyof PossessionPlayer,
      sortable: true,
    },
    {
      header: 'TO',
      key: 'turnovers' as keyof PossessionPlayer,
      sortable: true,
    },
    {
      header: 'CTO',
      key: 'causedTurnovers' as keyof PossessionPlayer,
      sortable: true,
    },
    {
      header: 'Shots Saved',
      key: 'shotsSaved' as keyof PossessionPlayer,
      sortable: true,
    },
    { header: 'NPC', key: 'npc' as keyof PossessionPlayer, sortable: true },
  ];

  const offenseColumns = [
    { ...createPlayerColumn<OffensePlayer>(), sortable: true },
    { header: 'S', key: 'shots' as keyof OffensePlayer, sortable: true },
    { header: 'SS', key: 'saved' as keyof OffensePlayer, sortable: true },
    {
      header: 'G',
      key: 'goals' as keyof OffensePlayer,
      sortable: true,
      render: (player: OffensePlayer) => (
        <div>
          <div>{player.goals}</div>
          <div className="text-xs text-gray-600">({player.shotPercentage.toFixed(0)}%)</div>
        </div>
      ),
    },
    {
      header: 'SOG',
      key: 'sogPercentage' as keyof OffensePlayer,
      sortable: true,
      render: (player: OffensePlayer) => <div className="text-xs text-gray-600">{player.sogPercentage}%</div>,
    },
    {
      header: 'A',
      key: 'assists' as keyof OffensePlayer,
      sortable: true,
    },
    {
      header: 'P',
      key: 'points' as keyof OffensePlayer,
      sortable: true,
    },
  ];

  const goalieColumns = [
    { ...createPlayerColumn<GoaliePlayer>(), sortable: true },
    {
      header: 'Shots on Goal',
      key: 'shotsOnGoal' as keyof GoaliePlayer,
      sortable: true,
    },
    { header: 'Saves', key: 'saved' as keyof GoaliePlayer, sortable: true },
    {
      header: 'SV%',
      key: 'savePercentage' as keyof GoaliePlayer,
      sortable: true,
      render: (player: GoaliePlayer) => (
        <div className="text-sm font-bold text-gray-600">{player.savePercentage.toFixed(0)}%</div>
      ),
    },
    {
      header: 'Goals Allowed',
      key: 'goalsAllowed' as keyof GoaliePlayer,
      sortable: true,
    },
  ];

  const blockedShotsColumns = [
    { ...createPlayerColumn<BlockedShotsPlayer>(), sortable: true },
    {
      header: 'Blocks',
      key: 'blocks' as keyof BlockedShotsPlayer,
      sortable: true,
    },
  ];

  // Fetch periods on component mount
  useEffect(() => {
    fetchPeriods();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameId]);

  // Fetch player stats when quarter changes
  useEffect(() => {
    if (periods.length > 0) {
      fetchPlayerStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeQuarter, periods, gameId]);

  const fetchPeriods = async () => {
    try {
      if (!clientId || !seasonId || !gameId) return;

      const response = await api.get(`${API_URLS.GET_GAME_PERIODS}/${gameId}/periods`, {
        params: { clientId, seasonId },
      });

      if (response.data.success) {
        const data = response.data.data;
        const hp = data.halfPeriod;
        const otFlag = data.otFlag;
        let periodArr: string[] = [];

        if (hp === true) {
          periodArr = ['Totals', 'H1', 'H2'];
        } else {
          periodArr = ['Totals', 'Q1', 'Q2', 'Q3', 'Q4'];
        }

        if (otFlag && !periodArr.includes('OT')) {
          periodArr.push('OT');
        }

        setPeriods(periodArr);
        setActiveQuarter('Totals');
      }
    } catch (error) {
      console.error('Failed to fetch periods:', error);
      setPeriods(['Totals', 'Q1', 'Q2', 'Q3', 'Q4']);
      setActiveQuarter('Totals');
    }
  };

  const processPossessionData = useCallback((playersData: any[]) => {
    const processed: PossessionPlayer[] = playersData
      .map((player) => {
        const groundBalls = player.groundballs || 0;
        const turnovers = player.turnovers || 0;
        const causedTurnovers = player.causedturnover || 0;
        const shotsSaved = player.shotsaved || 0;
        const position = player.position || [];

        // NPC Definition: NPC = Ground Balls - Net Turnovers (TO - CTOs) - Shots Saved
        // Net Turnovers = Turnovers - Caused Turnovers
        const netTurnovers = turnovers - causedTurnovers;
        const npc = groundBalls - netTurnovers - shotsSaved;
        const activity = groundBalls + turnovers + causedTurnovers + shotsSaved;

        return {
          playerId: player.playerSeasonId || player._id,
          playerNumber: player.no,
          playerName: `${player.firstName} ${player.lastName}`,
          groundBalls,
          turnovers,
          causedTurnovers,
          shotsSaved,
          activity,
          npc,
          position,
        };
      })
      .filter((player) => player.activity > 0);

    setPossessionPlayers(processed);
  }, []);

  const processOffenseData = useCallback((playersData: any[]) => {
    const processed: OffensePlayer[] = playersData
      .map((player) => {
        const shots = player.shots || 0;
        const saved = player.shotsaved || 0;
        const goals = player.goals || 0;
        const assists = player.assist || 0;
        const points = goals + assists;
        const shotPercentage = shots > 0 ? Number(((goals / shots) * 100).toFixed(0)) : 0;
        const sogPercentage = shots > 0 ? Math.ceil(((goals + saved) / shots) * 100) : 0;
        const position = player.position || [];

        const activity = shots + saved + goals + assists;

        return {
          playerId: player.playerSeasonId || player._id,
          playerNumber: player.no,
          playerName: `${player.firstName} ${player.lastName}`,
          shots,
          saved,
          goals,
          assists,
          points,
          shotPercentage,
          sogPercentage,
          activity,
          position,
        };
      })
      .filter((player) => player.activity > 0);

    setOffensePlayers(processed);
  }, []);

  const processGoalieData = useCallback((playersData: any[]) => {
    const processed: GoaliePlayer[] = playersData
      .filter((player) => player.shotsongoal)
      .map((player) => {
        const shotsOnGoal = player.shotsongoal || 0;
        const saved = player.saved || 0;
        const goalsAllowed = player.goalsallowed || 0;
        const savePercentage = shotsOnGoal > 0 ? Number(((saved / shotsOnGoal) * 100).toFixed(0)) : 0;
        return {
          playerId: player.playerSeasonId || player._id,
          playerNumber: player.no,
          playerName: `${player.firstName} ${player.lastName}`,
          shotsOnGoal,
          saved,
          goalsAllowed,
          savePercentage,
        };
      });

    setGoaliePlayers(processed);
  }, []);

  const processBlockedShotsData = useCallback((playersData: any[]) => {
    const processed: BlockedShotsPlayer[] = playersData
      .filter((player) => (player.blocks || 0) > 0)
      .map((player) => ({
        playerId: player.playerSeasonId || player._id,
        playerNumber: player.no,
        playerName: `${player.firstName} ${player.lastName}`,
        blocks: player.blocks || 0,
      }));

    setBlockedShotsPlayers(processed);
  }, []);

  const fetchPlayerStats = useCallback(async () => {
    if (!clientId || !seasonId || !activeQuarter) return;

    setIsRefreshing(true);
    try {
      let url = `${API_URLS.GET_PLAYER_STATS}?clientId=${clientId}&seasonId=${seasonId}&quarterId=${activeQuarter}`;
      if (gameId) {
        url += `&gameId=${gameId}`;
      }

      const response = await api.get(url);

      if (response.data.success) {
        const playersData = response.data.data;

        // Process and organize data by sections
        processPossessionData(playersData);
        processOffenseData(playersData);
        processGoalieData(playersData);
        setRawPlayers(Array.isArray(playersData) ? playersData : []);
        processBlockedShotsData(playersData);
      } else {
        showErrorMsg(response.data.message || 'Failed to fetch player statistics');
      }
    } catch (err: any) {
      showErrorMsg(err.response?.data?.message || err.message || 'Failed to fetch player statistics');
    } finally {
      setIsRefreshing(false);
    }
  }, [
    clientId,
    seasonId,
    activeQuarter,
    gameId,
    processPossessionData,
    processOffenseData,
    processGoalieData,
    processBlockedShotsData,
  ]);

  return (
    <div className="w-full bg-white min-h-screen">
      {/* Sticky Quarter Selector */}
      <QuarterSelector periods={periods} activeQuarter={activeQuarter} onSelectQuarter={setActiveQuarter} />

      {/* Main Content */}
      <div className="border border-sd-line bg-sd-light">
        <div className="flex flex-row items-center justify-between border-b border-sd-line px-4 py-4 text-[30px] font-bold">
            <p className="text-sd-navy">Players Report</p>
            <div
              className={`w-8 h-8 rounded-md p-1 cursor-pointer ${isRefreshing ? 'bg-gray-400' : 'bg-sd-accent'}`}
              onClick={isRefreshing ? undefined : fetchPlayerStats}
              title="Refresh data"
            >
              <img
                src="/icons/icon-refresh.png"
                alt="refresh"
                className={`w-full h-full ${isRefreshing ? 'opacity-50' : ''}`}
              />
            </div>
          </div>

          {/* Statistics Tables */}
          <div className="flex flex-col items-center w-full py-4 px-2">
            <div className="flex flex-col gap-3 max-w-[800px] w-full">
              <StatsTable
                title="Possession"
                columns={possessionColumns}
                players={possessionPlayers}
                isLoading={isRefreshing}
                sortConfig={possessionSortConfig}
                onSortChange={setPossessionSortConfig}
              />
              <StatsTable
                title="Offense"
                columns={offenseColumns}
                players={offensePlayers}
                isLoading={isRefreshing}
                sortConfig={offenseSortConfig}
                onSortChange={setOffenseSortConfig}
                cellPaddingY="py-0"
                cellPaddingX="px-0"
              />
              <StatsTable
                title="Goalie"
                columns={goalieColumns}
                players={goaliePlayers}
                isLoading={isRefreshing}
                sortConfig={goalieSortConfig}
                onSortChange={setGoalieSortConfig}
              />
              <WomensPlayerTables
                playersData={rawPlayers}
                seasonId={seasonId}
                layout="combined"
                renderTable={({ id, ...table }) => <StatsTable key={id} {...table} isLoading={isRefreshing} />}
              />
              <StatsTable
                title="Blocked Shots"
                columns={blockedShotsColumns}
                players={blockedShotsPlayers}
                isLoading={isRefreshing}
                sortConfig={blockedShotsSortConfig}
                onSortChange={setBlockedShotsSortConfig}
              />
            </div>
          </div>
      </div>
    </div>
  );
};

export default PlayersPageMobile;
